import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import WhatsAppCRMTab from './whatsapp/WhatsAppCRMTab';
import RespuestasRapidasConfig from './whatsapp/RespuestasRapidasConfig';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  MessageSquare, 
  Phone, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  RefreshCw,
  Settings,
  Save,
  Trash2,
  Send,
  DollarSign,
  Users,
  BarChart3,
  History,
  Megaphone,
  FileText,
  CreditCard,
  HelpCircle,
  User,
  Calendar,
  TrendingUp,
  Eye,
  X,
  AlertTriangle,
  CheckCircle2,
  Search,
  Headphones,
  UserCheck,
  MessageCircle,
  Paperclip,
  XCircle,
  File,
  Image,
  Download,
  Plus,
  Upload,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente de badge de notificación estilo iOS
function NotificationBadge({ count, onClick }) {
  if (!count || count === 0) return null;
  return (
    <span onClick={onClick} title="Ver conversaciones con mensajes no leídos" className="cursor-pointer absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow-sm"
      data-testid="crm-pendientes-badge">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function ConfigSection({ token }) {
  return (
    <Tabs defaultValue="general">
      <TabsList className="mb-4 flex-wrap h-auto gap-1">
        <TabsTrigger value="general" className="flex items-center gap-2" data-testid="config-subtab-general">
          <Settings className="w-4 h-4" /> General
        </TabsTrigger>
        <TabsTrigger value="estadisticas" className="flex items-center gap-2" data-testid="config-subtab-estadisticas">
          <BarChart3 className="w-4 h-4" /> Estadísticas
        </TabsTrigger>
        <TabsTrigger value="respuestas" className="flex items-center gap-2" data-testid="config-subtab-respuestas">
          <Zap className="w-4 h-4" /> Respuestas rápidas
        </TabsTrigger>
        <TabsTrigger value="sesiones" className="flex items-center gap-2" data-testid="config-subtab-sesiones">
          <Users className="w-4 h-4" /> Sesiones
        </TabsTrigger>
      </TabsList>
      <TabsContent value="general">
        <div className="mb-4 flex flex-wrap gap-2">
          <a href="/admin/bot-editor"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            data-testid="link-bot-editor">
            <Settings className="w-4 h-4" /> Bot Editor
          </a>
          <a href="/admin/ia-training"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100"
            data-testid="link-ia-training">
            <Settings className="w-4 h-4" /> IA Training
          </a>
        </div>
        <ConfigTab token={token} />
      </TabsContent>
      <TabsContent value="estadisticas">
        <EstadisticasTab token={token} />
      </TabsContent>
      <TabsContent value="respuestas">
        <RespuestasRapidasConfig token={token} />
      </TabsContent>
      <TabsContent value="sesiones">
        <SesionesTab token={token} />
      </TabsContent>
    </Tabs>
  );
}

export default function AdminWhatsAppPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('crm');
  const [pendientes, setPendientes] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroUrl = searchParams.get('filtro');
  const location = useLocation();
  useEffect(() => { if (filtroUrl) setActiveTab('crm'); }, [filtroUrl]);

  // Contador de takeovers + solicitudes pendientes (fuente de verdad: /counters)
  useEffect(() => {
    if (!token) return undefined;
    const fetchPendientes = async () => {
      try {
        const res = await axios.get(`${API_URL}/webhooks/twilio/admin/takeovers/counters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const alertas = res.data?.alertas || {};
        setPendientes(alertas.crm_atencion || 0);
      } catch (err) {
        console.error('Error fetching pendientes:', err);
      }
    };
    fetchPendientes();
    const interval = setInterval(fetchPendientes, 15000);
    return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Ayni</h1>
              <p className="text-green-100 text-sm">Panel de administración</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap h-auto gap-1">
            <TabsTrigger value="crm" className="flex items-center gap-2 relative" data-testid="wa-tab-crm">
              <MessageSquare className="w-4 h-4" />
              CRM
              <NotificationBadge count={pendientes} onClick={() => setSearchParams({ filtro: 'no_leidos' })} />
            </TabsTrigger>
            <TabsTrigger value="broadcast" className="flex items-center gap-2" data-testid="wa-tab-broadcast">
              <Megaphone className="w-4 h-4" />
              Broadcast
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2" data-testid="wa-tab-config">
              <Settings className="w-4 h-4" />
              Config
            </TabsTrigger>
          </TabsList>

          <TabsContent value="crm">
            <WhatsAppCRMTab token={token} filtroUrl={filtroUrl} filtroKey={location.key} />
          </TabsContent>

          <TabsContent value="broadcast">
            <BroadcastTab token={token} />
          </TabsContent>

          <TabsContent value="config">
            <ConfigSection token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ==================== ESTADÍSTICAS TAB ====================
function EstadisticasTab({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStats = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/webhooks/twilio/admin/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchStats(false), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, token]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Auto-refresh control */}
      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input 
            type="checkbox" 
            checked={autoRefresh} 
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded"
          />
          Auto-refresh (30s)
        </label>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{stats.total_sesiones}</p>
              <p className="text-sm text-gray-500">Total Sesiones</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <TrendingUp className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{stats.sesiones_activas_24h}</p>
              <p className="text-sm text-gray-500">Activas (24h)</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{stats.usuarios_registrados_whatsapp}</p>
              <p className="text-sm text-gray-500">Registrados vía WA</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-3xl font-bold text-gray-900">{stats.total_solicitudes_pendientes}</p>
              <p className="text-sm text-gray-500">Solicitudes Pendientes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Solicitudes por tipo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Solicitudes Pendientes por Tipo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <CreditCard className="w-6 h-6 mx-auto text-blue-600 mb-1" />
              <p className="text-2xl font-bold text-blue-900">{stats.solicitudes_pendientes?.plan_pago || 0}</p>
              <p className="text-xs text-blue-600">Plan de Pago</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg text-center">
              <AlertTriangle className="w-6 h-6 mx-auto text-red-600 mb-1" />
              <p className="text-2xl font-bold text-red-900">{stats.solicitudes_pendientes?.certificado_urgente || 0}</p>
              <p className="text-xs text-red-600">Certificado Urgente</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <MessageSquare className="w-6 h-6 mx-auto text-green-600 mb-1" />
              <p className="text-2xl font-bold text-green-900">{stats.solicitudes_pendientes?.contacto || 0}</p>
              <p className="text-xs text-green-600">Mensajes</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <DollarSign className="w-6 h-6 mx-auto text-amber-600 mb-1" />
              <p className="text-2xl font-bold text-amber-900">{stats.solicitudes_pendientes?.pago_adelantado || 0}</p>
              <p className="text-xs text-amber-600">Pago Adelantado</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mensajes por día */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Mensajes Últimos 7 Días
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-between h-40 gap-2">
            {stats.mensajes_por_dia?.map((dia, i) => {
              const max = Math.max(...stats.mensajes_por_dia.map(d => d.mensajes)) || 1;
              const height = (dia.mensajes / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-green-500 rounded-t transition-all"
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                  <p className="text-xs text-gray-500 mt-2">{dia.fecha}</p>
                  <p className="text-xs font-medium">{dia.mensajes}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Opciones más usadas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Opciones Más Usadas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats.opciones_populares?.slice(0, 10).map((op, i) => (
              <div key={i} className="flex items-center gap-3">
                <Badge variant="outline" className="w-8 text-center">{i + 1}</Badge>
                <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                  <div 
                    className="bg-green-500 h-full flex items-center px-2"
                    style={{ width: `${(op.usos / (stats.opciones_populares[0]?.usos || 1)) * 100}%` }}
                  >
                    <span className="text-xs text-white font-medium truncate">{op.opcion}</span>
                  </div>
                </div>
                <span className="text-sm text-gray-600 w-16 text-right">{op.usos} usos</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== BROADCAST TAB ====================
function BroadcastTab({ token }) {
  const [modo, setModo] = useState('manual'); // 'manual' o 'filtro'
  const [mensaje, setMensaje] = useState('');
  const [filtro, setFiltro] = useState('sesiones_activas');
  const [preview, setPreview] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Para modo manual
  const [telefonosTexto, setTelefonosTexto] = useState('');
  const [usarTemplate, setUsarTemplate] = useState(true);
  const [nombreTemplate, setNombreTemplate] = useState('');
  const [modoPersonalizado, setModoPersonalizado] = useState(false);
  const [datosPersonalizados, setDatosPersonalizados] = useState([
    { telefono: '', nombre: '', mensaje: '' }
  ]);

  // Template de WhatsApp aprobado (modelo)
  const TEMPLATE_MODELO = `Hola {{1}}, te escribimos desde el Colegio de Trabajadorxs Sociales de Lomas de Zamora.

{{2}}

Podés responder a este mensaje o escribir MENU para ver todas las opciones disponibles.`;

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  const fetchBroadcasts = async () => {
    try {
      const res = await axios.get(`${API_URL}/webhooks/twilio/admin/broadcasts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBroadcasts(res.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Parsear números del textarea
  const parsearTelefonos = () => {
    if (!telefonosTexto.trim()) return [];
    return telefonosTexto
      .split(/[\n,;]+/)
      .map(t => t.trim())
      .filter(t => t.length > 0);
  };

  // Funciones para manejar datos personalizados
  const agregarFilaPersonalizada = () => {
    setDatosPersonalizados([...datosPersonalizados, { telefono: '', nombre: '', mensaje: '' }]);
  };

  const eliminarFilaPersonalizada = (index) => {
    if (datosPersonalizados.length > 1) {
      setDatosPersonalizados(datosPersonalizados.filter((_, i) => i !== index));
    }
  };

  const actualizarFilaPersonalizada = (index, campo, valor) => {
    const nuevos = [...datosPersonalizados];
    nuevos[index][campo] = valor;
    setDatosPersonalizados(nuevos);
  };

  // Obtener teléfonos de datos personalizados
  const telefonosDePersonalizados = () => {
    return datosPersonalizados
      .filter(d => d.telefono.trim())
      .map(d => d.telefono.trim());
  };

  // Importar CSV
  const csvInputRef = React.useRef(null);
  
  const handleImportarCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        
        if (lines.length === 0) {
          alert('El archivo está vacío');
          return;
        }
        
        // Detectar separador (coma o punto y coma)
        const separador = lines[0].includes(';') ? ';' : ',';
        
        // Parsear header para detectar columnas
        const header = lines[0].toLowerCase().split(separador).map(h => h.trim().replace(/['"]/g, ''));
        
        // Buscar índices de columnas (flexible)
        let indexTelefono = header.findIndex(h => h.includes('telefono') || h.includes('teléfono') || h.includes('celular') || h.includes('phone') || h.includes('tel'));
        let indexNombre = header.findIndex(h => h.includes('nombre') || h.includes('name') || h === 'nombre');
        let indexMensaje = header.findIndex(h => h.includes('mensaje') || h.includes('message') || h.includes('msg'));
        
        // Si no hay header reconocible, asumir orden: teléfono, nombre, mensaje
        const tieneHeader = indexTelefono >= 0 || indexNombre >= 0;
        if (!tieneHeader) {
          indexTelefono = 0;
          indexNombre = 1;
          indexMensaje = 2;
        }
        
        // Parsear datos (saltar header si lo hay)
        const startLine = tieneHeader ? 1 : 0;
        const nuevosData = [];
        
        for (let i = startLine; i < lines.length; i++) {
          const cols = lines[i].split(separador).map(c => c.trim().replace(/^["']|["']$/g, ''));
          
          const telefono = cols[indexTelefono] || '';
          const nombre = indexNombre >= 0 ? (cols[indexNombre] || '') : '';
          const mensaje = indexMensaje >= 0 ? (cols[indexMensaje] || '') : '';
          
          if (telefono) {
            nuevosData.push({ telefono, nombre, mensaje });
          }
        }
        
        if (nuevosData.length === 0) {
          alert('No se encontraron datos válidos en el CSV');
          return;
        }
        
        setDatosPersonalizados(nuevosData);
        alert(`✅ Se importaron ${nuevosData.length} registros del CSV`);
        
      } catch (err) {
        console.error('Error parseando CSV:', err);
        alert('Error al leer el archivo CSV. Verificá el formato.');
      }
    };
    
    reader.readAsText(file);
    // Limpiar input para permitir reimportar el mismo archivo
    event.target.value = '';
  };

  const handlePreview = async () => {
    if (!mensaje.trim()) return alert('Escribí un mensaje');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/webhooks/twilio/admin/broadcast/preview`, 
        { mensaje, filtro },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setPreview(res.data);
    } catch (err) {
      alert('Error al obtener preview');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone.trim()) return alert('Ingresá un número de prueba');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/webhooks/twilio/admin/broadcast/test`, 
        { mensaje, filtro, test_phone: testPhone },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('Mensaje de prueba enviado');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Error al enviar'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnviar = async () => {
    setEnviando(true);
    try {
      const res = await axios.post(`${API_URL}/webhooks/twilio/admin/broadcast/enviar`, 
        { mensaje, filtro },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert(`Broadcast iniciado: ${res.data.destinatarios} destinatarios`);
      setShowConfirm(false);
      setMensaje('');
      setPreview(null);
      fetchBroadcasts();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Error al enviar'));
    } finally {
      setEnviando(false);
    }
  };

  const handleEnviarManual = async () => {
    // Si es modo personalizado, usar datos de la tabla
    const telefonos = modoPersonalizado ? telefonosDePersonalizados() : parsearTelefonos();
    
    if (telefonos.length === 0) return alert('Ingresá al menos un número');
    if (!mensaje.trim()) return alert('Escribí un mensaje');
    if (usarTemplate && !modoPersonalizado && !nombreTemplate.trim()) return alert('Ingresá el nombre para el template');
    
    // En modo personalizado, verificar que todos tengan nombre
    if (modoPersonalizado && usarTemplate) {
      const sinNombre = datosPersonalizados.filter(d => d.telefono.trim() && !d.nombre.trim());
      if (sinNombre.length > 0) {
        return alert('Completá el nombre para todos los destinatarios');
      }
    }
    
    setEnviando(true);
    try {
      const payload = { 
        telefonos, 
        mensaje,
        nombre_template: usarTemplate && !modoPersonalizado ? nombreTemplate : 'Colega',
        usar_template: usarTemplate
      };
      
      // Agregar datos personalizados si corresponde
      if (modoPersonalizado) {
        payload.datos_personalizados = datosPersonalizados
          .filter(d => d.telefono.trim())
          .map(d => ({
            telefono: d.telefono.trim(),
            nombre: d.nombre.trim(),
            mensaje: d.mensaje.trim() || '' // Si está vacío, se usa el mensaje general
          }));
      }
      
      const res = await axios.post(`${API_URL}/webhooks/twilio/admin/broadcast/manual`, 
        payload,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      let alertMsg = `Broadcast iniciado: ${res.data.destinatarios} destinatarios`;
      alertMsg += `\nUsando ${res.data.usar_template ? 'template aprobado' : 'mensaje libre'}`;
      if (res.data.con_datos_personalizados) alertMsg += '\nCon datos personalizados por destinatario';
      
      alert(alertMsg);
      setShowConfirm(false);
      setMensaje('');
      setNombreTemplate('');
      setTelefonosTexto('');
      setDatosPersonalizados([{ telefono: '', nombre: '', mensaje: '' }]);
      fetchBroadcasts();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Error al enviar'));
    } finally {
      setEnviando(false);
    }
  };

  const telefonosParsed = parsearTelefonos();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Composer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-green-600" />
            Envío Masivo
          </CardTitle>
          <CardDescription>Enviá mensajes a múltiples destinatarios</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Selector de modo */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setModo('manual')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                modo === 'manual' 
                  ? 'bg-white shadow text-green-700' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📝 Números Manuales
            </button>
            <button
              onClick={() => setModo('filtro')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                modo === 'filtro' 
                  ? 'bg-white shadow text-green-700' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              🔍 Por Filtro
            </button>
          </div>

          {/* Campo de mensaje - diferente según modo y si usa template */}
          {modo === 'manual' && usarTemplate ? (
            null  // El mensaje se muestra dentro del bloque del template
          ) : (
            <div>
              <Label>Mensaje</Label>
              <Textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder="Escribí el mensaje que querés enviar..."
                rows={4}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">{mensaje.length} caracteres (máx 1000)</p>
            </div>
          )}

          {modo === 'manual' ? (
            <>
              {/* Selector de modo: simple vs personalizado */}
              <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-sm font-medium text-blue-700">Modo de ingreso:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!modoPersonalizado}
                    onChange={() => setModoPersonalizado(false)}
                    className="text-blue-600"
                  />
                  <span className="text-sm">Simple (mismo nombre para todos)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={modoPersonalizado}
                    onChange={() => setModoPersonalizado(true)}
                    className="text-blue-600"
                  />
                  <span className="text-sm">Personalizado (nombre por destinatario)</span>
                </label>
              </div>

              {!modoPersonalizado ? (
                /* Modo simple: textarea con números */
                <div>
                  <Label>Números de Teléfono</Label>
                  <Textarea
                    value={telefonosTexto}
                    onChange={(e) => setTelefonosTexto(e.target.value)}
                    placeholder="Ingresá los números, uno por línea o separados por coma:&#10;+5491112345678&#10;1156789012&#10;011-4567-8901"
                    rows={4}
                    className="mt-1 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {telefonosParsed.length} número{telefonosParsed.length !== 1 ? 's' : ''} detectado{telefonosParsed.length !== 1 ? 's' : ''}
                  </p>
                </div>
              ) : (
                /* Modo personalizado: tabla con teléfono y nombre */
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label>Destinatarios con datos personalizados</Label>
                    <div className="flex items-center gap-2">
                      {/* Input oculto para CSV */}
                      <input
                        type="file"
                        ref={csvInputRef}
                        accept=".csv,.txt"
                        onChange={handleImportarCSV}
                        className="hidden"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => csvInputRef.current?.click()}
                        className="text-xs"
                      >
                        <Upload className="w-3 h-3 mr-1" /> Importar CSV
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={agregarFilaPersonalizada}
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Agregar fila
                      </Button>
                    </div>
                  </div>
                  
                  {/* Instrucciones CSV */}
                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border">
                    📄 <strong>Formato CSV:</strong> columnas <code>telefono</code>, <code>nombre</code>, <code>mensaje</code> (separadas por coma o punto y coma)
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Teléfono *</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Nombre {'{{1}}'} *</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Mensaje {'{{2}}'} <span className="font-normal text-gray-500">(opcional)</span></th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {datosPersonalizados.map((fila, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-2 py-1">
                              <Input
                                value={fila.telefono}
                                onChange={(e) => actualizarFilaPersonalizada(index, 'telefono', e.target.value)}
                                placeholder="1156789012"
                                className="font-mono text-xs h-8"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                value={fila.nombre}
                                onChange={(e) => actualizarFilaPersonalizada(index, 'nombre', e.target.value)}
                                placeholder="María"
                                className="text-xs h-8"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                value={fila.mensaje}
                                onChange={(e) => actualizarFilaPersonalizada(index, 'mensaje', e.target.value)}
                                placeholder="(usa mensaje general si vacío)"
                                className="text-xs h-8"
                              />
                            </td>
                            <td className="px-1">
                              {datosPersonalizados.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => eliminarFilaPersonalizada(index)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500">
                    {telefonosDePersonalizados().length} destinatario{telefonosDePersonalizados().length !== 1 ? 's' : ''} con teléfono válido
                  </p>
                  <p className="text-xs text-blue-600">
                    💡 Si dejás el campo "Mensaje" vacío, se usará el mensaje general de abajo para ese destinatario.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                <input
                  type="checkbox"
                  id="usarTemplate"
                  checked={usarTemplate}
                  onChange={(e) => setUsarTemplate(e.target.checked)}
                  className="w-4 h-4 text-violet-600 rounded"
                />
                <label htmlFor="usarTemplate" className="text-sm cursor-pointer">
                  <span className="font-medium text-violet-700">Usar template aprobado de WhatsApp</span>
                  <p className="text-xs text-violet-600 mt-0.5">
                    Recomendado para contactar usuarios sin conversación reciente (últimas 24hs)
                  </p>
                </label>
              </div>

              {usarTemplate ? (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FileText className="w-4 h-4" />
                    Campos del Template Aprobado
                  </div>
                  
                  {/* Modelo del template */}
                  <div className="p-3 bg-white rounded border text-xs text-gray-600 font-mono whitespace-pre-wrap">
                    {TEMPLATE_MODELO}
                  </div>
                  
                  {/* Campo 1: Nombre - solo si NO es modo personalizado */}
                  {!modoPersonalizado && (
                    <div>
                      <Label className="text-sm">
                        <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-xs mr-2">{'{{1}}'}</span>
                        Nombre / Saludo
                      </Label>
                      <Input
                        value={nombreTemplate}
                        onChange={(e) => setNombreTemplate(e.target.value)}
                        placeholder="Ej: Colega, María, Matriculade"
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Se usará en: "Hola <strong>{nombreTemplate || '{{1}}'}</strong>, te escribimos..."
                      </p>
                    </div>
                  )}
                  
                  {modoPersonalizado && (
                    <div className="p-3 bg-green-50 rounded border border-green-200">
                      <p className="text-sm text-green-700">
                        ✅ El campo <strong>{'{{1}}'}</strong> (nombre) se tomará de la tabla de destinatarios
                      </p>
                    </div>
                  )}
                  
                  {/* Campo 2: Mensaje */}
                  <div>
                    <Label className="text-sm">
                      <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-xs mr-2">{'{{2}}'}</span>
                      Mensaje {modoPersonalizado && <span className="font-normal text-gray-500">(se usa si la fila no tiene mensaje propio)</span>}
                    </Label>
                    <Textarea
                      value={mensaje}
                      onChange={(e) => setMensaje(e.target.value)}
                      placeholder="Escribí el contenido del mensaje..."
                      rows={3}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">{mensaje.length} caracteres (máx 1000)</p>
                  </div>
                  
                  {/* Preview del mensaje final */}
                  {(nombreTemplate || mensaje || modoPersonalizado) && (
                    <div className="mt-2">
                      <Label className="text-xs text-gray-600">Vista previa del mensaje:</Label>
                      <div className="p-3 bg-green-50 rounded border border-green-200 text-sm mt-1 whitespace-pre-wrap">
                        Hola <strong>{modoPersonalizado ? '[nombre de cada destinatario]' : (nombreTemplate || '{{1}}')}</strong>, te escribimos desde el Colegio de Trabajadorxs Sociales de Lomas de Zamora.{'\n\n'}{mensaje || '{{2}}'}{'\n\n'}Podés responder a este mensaje o escribir MENU para ver todas las opciones disponibles.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <Label>Mensaje Libre</Label>
                  <Textarea
                    value={mensaje}
                    onChange={(e) => setMensaje(e.target.value)}
                    placeholder="Escribí el mensaje que querés enviar..."
                    rows={4}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">{mensaje.length} caracteres</p>
                  <p className="text-xs text-amber-600 mt-1">⚠️ Solo funciona si el usuario tuvo conversación en las últimas 24hs</p>
                </div>
              )}

              {telefonosParsed.length > 0 && !modoPersonalizado && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800 text-sm">
                    ✓ {telefonosParsed.length} destinatario{telefonosParsed.length !== 1 ? 's' : ''}
                  </p>
                  <div className="text-xs text-green-600 mt-1 max-h-20 overflow-y-auto">
                    {telefonosParsed.slice(0, 10).map((t, i) => (
                      <span key={i} className="inline-block mr-2">{t}</span>
                    ))}
                    {telefonosParsed.length > 10 && <span>...y {telefonosParsed.length - 10} más</span>}
                  </div>
                </div>
              )}

              <Button 
                onClick={() => setShowConfirm(true)} 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={
                  loading || 
                  !mensaje.trim() || 
                  (modoPersonalizado ? telefonosDePersonalizados().length === 0 : telefonosParsed.length === 0) || 
                  (usarTemplate && !modoPersonalizado && !nombreTemplate.trim())
                }
              >
                <Send className="w-4 h-4 mr-2" />
                Enviar a {modoPersonalizado ? telefonosDePersonalizados().length : telefonosParsed.length} número{(modoPersonalizado ? telefonosDePersonalizados().length : telefonosParsed.length) !== 1 ? 's' : ''}
              </Button>
            </>
          ) : (
            <>
              <div>
                <Label>Destinatarios</Label>
                <select
                  value={filtro}
                  onChange={(e) => { setFiltro(e.target.value); setPreview(null); }}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                >
                  <option value="sesiones_activas">Usuarios que usaron WhatsApp</option>
                  <option value="con_usuario">Matriculados con cuenta creada</option>
                  <option value="todos">Todos los matriculados con celular</option>
                </select>
              </div>

              <Button onClick={handlePreview} disabled={loading || !mensaje.trim()} className="w-full">
                <Eye className="w-4 h-4 mr-2" />
                Ver Preview
              </Button>

              {preview && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800">Destinatarios: {preview.destinatarios}</p>
                  <p className="text-sm text-green-600 mt-2">Preview:</p>
                  <p className="text-sm bg-white p-2 rounded mt-1 whitespace-pre-wrap">{preview.mensaje_preview}</p>
                </div>
              )}

              <div className="pt-4 border-t space-y-3">
                <Label>Envío de Prueba</Label>
                <div className="flex gap-2">
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+54911..."
                    className="flex-1"
                  />
                  <Button variant="outline" onClick={handleTest} disabled={loading || !mensaje.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {preview && preview.destinatarios > 0 && (
                <Button 
                  onClick={() => setShowConfirm(true)} 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  <Megaphone className="w-4 h-4 mr-2" />
                  Enviar a {preview.destinatarios} destinatarios
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Historial de Broadcasts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Envíos
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchBroadcasts}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {broadcasts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay envíos anteriores</p>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {broadcasts.map((bc) => (
                <div key={bc.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={bc.estado === 'completado' ? 'default' : 'secondary'}>
                        {bc.estado === 'completado' ? '✓ Completado' : '⏳ En proceso'}
                      </Badge>
                      {bc.filtro === 'manual' && (
                        <Badge variant="outline" className="text-violet-600 border-violet-300">
                          Manual
                        </Badge>
                      )}
                      {bc.usar_template && (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                          Template
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(bc.created_at).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2">{bc.mensaje}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="text-green-600">✓ {bc.estadisticas?.enviados || 0}</span>
                    <span className="text-red-600">✗ {bc.estadisticas?.fallidos || 0}</span>
                    <span>Total: {bc.total_destinatarios}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Envío Masivo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {modo === 'manual' ? (
              <>
                <p>Estás por enviar un mensaje a <strong>{telefonosParsed.length}</strong> número{telefonosParsed.length !== 1 ? 's' : ''}.</p>
                {usarTemplate && (
                  <div className="p-2 bg-violet-50 rounded text-sm text-violet-700">
                    ✓ Se usará el template aprobado de WhatsApp
                  </div>
                )}
              </>
            ) : (
              <p>Estás por enviar un mensaje a <strong>{preview?.destinatarios}</strong> destinatarios.</p>
            )}
            <div className="p-3 bg-gray-50 rounded-lg text-sm">
              <p className="whitespace-pre-wrap">{mensaje}</p>
            </div>
            <p className="text-sm text-amber-600">Esta acción no se puede deshacer.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            <Button 
              onClick={modo === 'manual' ? handleEnviarManual : handleEnviar} 
              disabled={enviando} 
              className="bg-green-600 hover:bg-green-700"
            >
              {enviando ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Confirmar Envío
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== SESIONES TAB ====================
function SesionesTab({ token }) {
  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchSesiones = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/webhooks/twilio/admin/sesiones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSesiones(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSesiones();
  }, []);

  // Auto-refresh cada 10 segundos
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchSesiones(false), 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, token]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-green-600" />
          Sesiones Activas ({sesiones.length})
        </CardTitle>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <Button variant="outline" size="sm" onClick={() => fetchSesiones(true)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
          </div>
        ) : sesiones.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 mx-auto text-gray-300" />
            <p className="text-gray-500 mt-2">No hay sesiones activas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Teléfono</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Estado</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Usuario</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Último mensaje</th>
                </tr>
              </thead>
              <tbody>
                {sesiones.map((s, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <span className="font-mono text-sm">{s.phone?.replace('whatsapp:', '')}</span>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline">{s.estado}</Badge>
                    </td>
                    <td className="py-2 px-3">
                      {s.contexto?.tiene_usuario ? (
                        <Badge className="bg-green-100 text-green-700">Con cuenta</Badge>
                      ) : (
                        <Badge variant="secondary">Sin cuenta</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-500">
                      {s.ultimo_mensaje ? new Date(s.ultimo_mensaje).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== CONFIG TAB ====================
function ConfigTab({ token }) {
  const [config, setConfig] = useState({ valor_1_cbt: 440226, valor_2_cbt: 880452 });
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/webhooks/twilio/admin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) setConfig(res.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const guardarConfig = async () => {
    setGuardando(true);
    try {
      await axios.post(`${API_URL}/webhooks/twilio/admin/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Configuración guardada');
    } catch (err) {
      alert('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Valores de Canasta Básica Total (CBT)
          </CardTitle>
          <CardDescription>
            Estos valores se usan para calcular las bonificaciones por ingresos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Valor 1 CBT ($)</Label>
              <Input
                type="number"
                value={config.valor_1_cbt}
                onChange={(e) => setConfig({...config, valor_1_cbt: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500 mt-1">Bonificación 40% para ingresos hasta este valor</p>
            </div>
            <div>
              <Label>Valor 2 CBT ($)</Label>
              <Input
                type="number"
                value={config.valor_2_cbt}
                onChange={(e) => setConfig({...config, valor_2_cbt: parseInt(e.target.value) || 0})}
              />
              <p className="text-xs text-gray-500 mt-1">Bonificación 30% para ingresos hasta este valor</p>
            </div>
          </div>

          <Button onClick={guardarConfig} disabled={guardando} className="bg-green-600 hover:bg-green-700">
            {guardando ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </CardContent>
      </Card>

      {/* Card de Oficina Cerrada por Imprevisto */}
      <OficinaCerradaCard token={token} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración de Twilio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label className="text-gray-600">URL del Webhook</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 p-2 bg-gray-100 rounded text-sm font-mono overflow-x-auto">
                {process.env.REACT_APP_BACKEND_URL}/api/webhooks/twilio/whatsapp
              </code>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/twilio/whatsapp`);
                  alert('URL copiada');
                }}
              >
                Copiar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== OFICINA CERRADA CARD ====================
function OficinaCerradaCard({ token }) {
  const [cerrada, setCerrada] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [desde, setDesde] = useState(null);
  const [por, setPor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    fetchEstado();
  }, []);

  const fetchEstado = async () => {
    try {
      const res = await axios.get(`${API_URL}/webhooks/twilio/admin/bot-config/oficina-cerrada`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCerrada(res.data.cerrada);
      setMotivo(res.data.motivo || '');
      setDesde(res.data.desde);
      setPor(res.data.por);
    } catch (err) {
      console.error('Error obteniendo estado oficina:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    const nuevoCerrada = !cerrada;
    
    if (nuevoCerrada && !motivo.trim()) {
      alert('Por favor ingresá un motivo para cerrar la oficina');
      return;
    }

    setGuardando(true);
    try {
      await axios.post(`${API_URL}/webhooks/twilio/admin/bot-config/oficina-cerrada`, {
        cerrada: nuevoCerrada,
        motivo: nuevoCerrada ? motivo.trim() : ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCerrada(nuevoCerrada);
      if (!nuevoCerrada) {
        setMotivo('');
        setDesde(null);
        setPor(null);
      } else {
        setDesde(new Date().toISOString());
      }
      
      alert(nuevoCerrada 
        ? '🔴 Oficina marcada como cerrada. El bot informará a los usuarios.' 
        : '🟢 Oficina abierta. El bot operará normalmente.'
      );
    } catch (err) {
      console.error('Error actualizando estado:', err);
      alert('Error al actualizar el estado');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <RefreshCw className="w-6 h-6 mx-auto animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cerrada ? 'border-red-300 bg-red-50' : ''}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {cerrada ? (
            <AlertTriangle className="w-5 h-5 text-red-600" />
          ) : (
            <Clock className="w-5 h-5 text-green-600" />
          )}
          Oficina Cerrada por Imprevisto
        </CardTitle>
        <p className="text-sm text-gray-500">
          Activá esta opción si por algún imprevisto la oficina no puede atender hoy 
          (paro, corte de luz, etc.). El bot informará a los usuarios que la oficina 
          está temporalmente cerrada.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {cerrada && desde && (
          <div className="p-3 bg-red-100 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Oficina cerrada desde:</span>
              <span>{new Date(desde).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</span>
            </div>
            {por && (
              <p className="text-sm text-red-700 mt-1">
                Activado por: {por}
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label>Motivo del cierre</Label>
          <Input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: Paro de ATE, corte de luz, feriado no previsto..."
            disabled={cerrada}
          />
          <p className="text-xs text-gray-500">
            Este mensaje se mostrará a los usuarios que intenten comunicarse con administración
          </p>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${cerrada ? 'bg-red-500' : 'bg-green-500'}`} />
            <span className="font-medium">
              Estado: {cerrada ? 'Oficina CERRADA' : 'Oficina ABIERTA (horario normal)'}
            </span>
          </div>
          
          <Button
            onClick={handleToggle}
            disabled={guardando}
            variant={cerrada ? 'default' : 'destructive'}
            className={cerrada ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {guardando ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : cerrada ? (
              <CheckCircle className="w-4 h-4 mr-2" />
            ) : (
              <XCircle className="w-4 h-4 mr-2" />
            )}
            {cerrada ? 'Reabrir Oficina' : 'Cerrar Oficina'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
